import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import Admin from './components/Admin';
import { PermissionProvider } from './components/admin/PermissionContext';
import SchoolAdmin from './components/SchoolAdmin';
import InstitutionSelector from './components/InstitutionSelector';
import SuperAdminLogin from './components/SuperAdminLogin';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import StudentRegister from './components/StudentRegister';
import inactivityTimerService from './services/InactivityTimerService';
import InactivityWarningModal from './components/common/InactivityWarningModal';
import './App.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndLogin = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          <div style={{ maxWidth: '600px', width: '100%', background: '#ffffff', borderRadius: '12px', padding: '32px', boxShadow: '0 10px 25px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '16px' }}>
              ⚠️
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px 0' }}>Dashboard Loading Notice</h2>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0', lineHeight: '1.5' }}>
              A session or rendering issue occurred. You can reload or reset the session to continue.
            </p>
            {this.state.error && (
              <div style={{ background: '#f1f5f9', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', color: '#b91c1c', fontFamily: 'monospace', marginBottom: '20px', overflowX: 'auto' }}>
                {this.state.error.toString()}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button onClick={this.handleReload} style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Reload Page
              </button>
              <button onClick={this.handleClearAndLogin} style={{ padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Clear Session & Re-Login
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuperAuthenticated, setIsSuperAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [selectedInstitutionType, setSelectedInstitutionType] = useState(null);

  // Inactivity State
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [inactivityRemainingSeconds, setInactivityRemainingSeconds] = useState(120);

  useEffect(() => {
    // Restore authentication state from localStorage on page reload
    const token = localStorage.getItem('authToken');
    const storedOrgData = localStorage.getItem('orgData');
    const superToken = localStorage.getItem('superAdminToken');

    if (token) {
      setIsAuthenticated(true);
      if (storedOrgData) {
        try {
          setOrgData(JSON.parse(storedOrgData));
        } catch (e) {
          console.error("Failed to parse orgData from localStorage:", e);
        }
      }
    }

    if (superToken) {
      setIsSuperAuthenticated(true);
    }

    setLoading(false);
  }, []);

  // Inactivity Auto-Logout Lifecycle
  useEffect(() => {
    const isAnyAuth = isAuthenticated || isSuperAuthenticated;
    if (isAnyAuth) {
      inactivityTimerService.start({
        timeoutMs: 30 * 60 * 1000, // 30 minutes
        warningMs: 2 * 60 * 1000,  // 2 minutes
        onWarning: (remainingSecs) => {
          setInactivityRemainingSeconds(remainingSecs);
          setShowInactivityWarning(true);
        },
        onActivity: () => {
          setShowInactivityWarning(false);
        },
        onTimeout: ({ isCrossTab }) => {
          setShowInactivityWarning(false);
          handleLogout();
          handleSuperLogout();
        }
      });
    } else {
      inactivityTimerService.stop();
      setShowInactivityWarning(false);
    }

    return () => {
      inactivityTimerService.stop();
    };
  }, [isAuthenticated, isSuperAuthenticated]);

  useEffect(() => {
    const handleOrgDataUpdate = () => {
      const storedOrgData = localStorage.getItem('orgData');
      if (storedOrgData) {
        try {
          setOrgData(JSON.parse(storedOrgData));
        } catch (e) {
          console.error("Failed to parse updated orgData from localStorage:", e);
        }
      }
    };
    window.addEventListener('orgDataChanged', handleOrgDataUpdate);
    return () => {
      window.removeEventListener('orgDataChanged', handleOrgDataUpdate);
    };
  }, []);

  const handleLogin = (token, organizationData = null) => {
    localStorage.setItem('authToken', token);
    if (organizationData) {
      localStorage.setItem('orgData', JSON.stringify(organizationData));
      if (organizationData.orgId) {
        localStorage.setItem('orgId', String(organizationData.orgId));
      }
      setOrgData(organizationData);
    }
    setSelectedInstitutionType(null); // Reset institution selection on login
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('orgData');
    setIsAuthenticated(false);
    setOrgData(null);
    setSelectedInstitutionType(null);
    setShowInactivityWarning(false);
  };

  const handleSuperLogin = (token) => {
    setIsSuperAuthenticated(true);
  };

  const handleSuperLogout = () => {
    localStorage.removeItem('superAdminToken');
    setIsSuperAuthenticated(false);
    setShowInactivityWarning(false);
  };

  const handleContinueSession = () => {
    setShowInactivityWarning(false);
    inactivityTimerService.reset();
  };

  const handleInstitutionSelect = (institutionType) => {
    setSelectedInstitutionType(institutionType);
  };

  // Function to determine which dashboard to render
  const getDashboardComponent = () => {
    // Both School and College share the unified modern UI theme and portal
    return (
      <PermissionProvider>
        <SchoolAdmin onLogout={handleLogout} orgData={orgData} />
      </PermissionProvider>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const basename = window.location.pathname.startsWith('/build') ? '/build' : '';

  return (
    <ErrorBoundary>
      <Router basename={basename}>
        <div className="App">
          <Routes>
            <Route 
              path="/login" 
              element={
                !isAuthenticated ? 
                  <Login onLogin={handleLogin} /> : 
                  <Navigate to="/admin" replace />
              } 
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/register/:orgId"
              element={<StudentRegister />}
            />
            <Route 
              path="/admin" 
              element={
                isAuthenticated ? 
                  getDashboardComponent() : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/superadmin/login" 
              element={
                !isSuperAuthenticated ? 
                  <SuperAdminLogin onLogin={handleSuperLogin} /> : 
                  <Navigate to="/superadmin" replace />
              } 
            />
            <Route 
              path="/superadmin" 
              element={
                isSuperAuthenticated ? 
                  <SuperAdminDashboard onLogout={handleSuperLogout} /> : 
                  <Navigate to="/superadmin/login" replace />
              } 
            />
            <Route 
              path="/" 
              element={<Navigate to={isAuthenticated ? "/admin" : "/login"} replace />} 
            />
          </Routes>

          {/* Session Inactivity Warning Modal */}
          <InactivityWarningModal
            isOpen={showInactivityWarning}
            remainingSeconds={inactivityRemainingSeconds}
            onContinueSession={handleContinueSession}
            onLogoutNow={() => {
              handleLogout();
              handleSuperLogout();
            }}
          />
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
